/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  BENEFIT_BASE_URL,
  RD_PROFILE_BASE_URL,
  RD_PROGRAM_PROFILE_BASE_URL,
} from "Constants/SagaConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IClaimBenefitDataResult } from "Interfaces/Benefits/IClaimBenefitDataResult";
import { IExtendAzureBenefitCreditDataResult } from "Interfaces/Benefits/IExtendAzureBenefitCreditDataResult";
import { IM365BenefitDetail } from "Interfaces/Benefits/IM365BenefitDetail";
import { IM365BenefitDetailDataResult } from "Interfaces/Benefits/IM365BenefitDetailDataResult";
import { IMicrosoft365BenefitDetail } from "Interfaces/Benefits/IMicrosoft365BenefitDetail";
import { IMicrosoft365BenefitDetailDataResult } from "Interfaces/Benefits/IMicrosoft365BenefitDetailDataResult";
import { IProfileBenefitDataResult } from "Interfaces/Benefits/IProfileBenefitDataResult";
import { ICommentAddRequest } from "Interfaces/Comments/ICommentAddRequest";
import { ICommentAddResult } from "Interfaces/Comments/ICommentAddResult";
import { ICommentsGetResult } from "Interfaces/Comments/ICommentsGetResult";
import { IApiEmptyResult, IApiResult2 } from "Interfaces/IApiResult";
import { IProgramProfileUpdateRequest } from "Interfaces/ProgramProfile/IProgramProfileUpdateRequest";
import { IProgramProfileUpdateResult } from "Interfaces/ProgramProfile/IProgramProfileUpdateResult";
import { IGetRDUserProfilesRequest } from "Interfaces/RD/UserProfile/IGetRDUserProfilesRequest";
import { IRDAzureBenefitClaim } from "Interfaces/RD/IRDAzureBenefitClaim";
import { IRDUserProfileGetResult } from "Interfaces/RD/UserProfile/IRDUserProfileGetResult";
import { IRDUserProfilesSearchResult } from "Interfaces/RD/UserProfile/IRDUserProfilesSearchResult";
import { IUserProfileDataResult } from "Interfaces/UserProfile/IUserProfileDataResult";
import { get, post, put, IHttpResponse } from "Services/ApiUtils";

/**
 * Search all the user profiles for entered text.
 * @param {IGetRDUserProfilesRequest} searchRequest - The search request
 */
export async function searchRDUserProfiles(
  searchRequest: IGetRDUserProfilesRequest
): Promise<IHttpResponse<IApiResult2<IRDUserProfilesSearchResult>>> {
  return await post<IApiResult2<IRDUserProfilesSearchResult>>(
    RD_PROFILE_BASE_URL + "search",
    searchRequest,
    true
  );
}

/**
 * Download user profiles.
 * @param {IGetRDUserProfilesRequest} request The request
 */
export async function downloadRDUserProfiles(
  request: IGetRDUserProfilesRequest
): Promise<IHttpResponse<IApiResult2<string>>> {
  return await post<IApiResult2<string>>(RD_PROFILE_BASE_URL + "download", request, true, false, {
    method: "POST",
    body: JSON.stringify(request),
    headers: { "Content-Type": "application/octet-stream" },
  });
}

/**
 * Fetch RD user profile.
 * @param {number} rdUserProfileId The user profile id to fetch.
 */
export async function fetchRDUserProfile(
  rdUserProfileId: number
): Promise<IHttpResponse<IApiResult2<IRDUserProfileGetResult>>> {
  return await get<IApiResult2<IRDUserProfileGetResult>>(
    RD_PROFILE_BASE_URL + rdUserProfileId,
    true
  );
}

/**
 * Fetch RD user profile comments.
 * @param {number} rdUserProfileId The user profile id to fetch.
 */
export async function fetchRDUserProfileComments(
  rdUserProfileId: number
): Promise<IHttpResponse<IApiResult2<ICommentsGetResult>>> {
  return await get<IApiResult2<ICommentsGetResult>>(
    RD_PROFILE_BASE_URL + rdUserProfileId + "/Comments",
    true
  );
}

/**
 * Add RD user profile comment.
 * @param {ICommentAddRequest} request The request.
 */
export async function addRDUserProfileComment(
  request: ICommentAddRequest
): Promise<IHttpResponse<IApiResult2<ICommentAddResult>>> {
  return await post<IApiResult2<ICommentAddResult>>(
    RD_PROFILE_BASE_URL + request.userProfileComment.userProfileId + "/Comments",
    request,
    true
  );
}

/**
 * Get RD Program profile.
 * @param {userProfileId} number The request.
 */
export async function getRDProgramProfile(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<any>>> {
  return await get<IApiResult2<any>>(RD_PROGRAM_PROFILE_BASE_URL + userProfileId, true);
}

/**
 * Update RD Program profile.
 * @param {IProgramProfileUpdateRequest} request The request.
 */
export async function updateRDProgramProfile(
  request: IProgramProfileUpdateRequest
): Promise<IHttpResponse<IApiResult2<IProgramProfileUpdateResult>>> {
  return await put<IApiResult2<IProgramProfileUpdateResult>>(
    RD_PROGRAM_PROFILE_BASE_URL,
    request,
    true
  );
}

/**
 * Claims the github pro benefit for the user profile.
 * @param {number} userProfileId The profile id.
 * @returns {IClaimBenefitDataResult}
 */
export async function claimUserProfileBenefitsGithubPro(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.RD.toLowerCase())}github`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the linkedin benefit for the user profile.
 * @param userProfileId the profile id.
 * @returns {IClaimBenefitDataResult}
 */
export async function claimUserProfileBenefitsLinkedIn(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.RD.toLowerCase())}linkedin`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the M365 benefit for the user.
 * @param userProfileId the user profile to claim the benefit for.
 * @returns {IClaimBenefitDataResult}
 */
export async function claimUserProfileBenefitsM365(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.RD.toLowerCase())}m365`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the MS365 benefit for the user.
 * @param userProfileId the user profile to claim the benefit for.
 * @returns {IClaimBenefitDataResult}
 */
export async function claimUserProfileBenefitsMS365(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.RD.toLowerCase())}ms365`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Gets a users Microsoft 365 Benefit Details.
 * @param userProfileId the userprofile id to fetch the benefit details for.
 * @returns {IMicrosoft365BenefitDetailDataResult}
 */
export async function getMS365BenefitDetails(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IMicrosoft365BenefitDetailDataResult>>> {
  return await get<IApiResult2<IMicrosoft365BenefitDetailDataResult>>(
    `${BENEFIT_BASE_URL.replace(
      "{tenant}",
      TenantTypesEnum.RD.toLowerCase()
    )}ms365detail/${userProfileId}`,
    true
  );
}

/**
 * Updates the users Microsoft 365 Benefit Details that helps with renewing the MS365 Benefit.
 * @param ms365BenefitDetails the ms365 subscription details to update/add.
 * @returns {IMicrosoft365BenefitDetailDataResult}
 */
export async function updateMS365BenefitDetails(
  m365BenefitDetails: IMicrosoft365BenefitDetail
): Promise<IHttpResponse<IApiResult2<IMicrosoft365BenefitDetailDataResult>>> {
  return await put<IApiResult2<IMicrosoft365BenefitDetailDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.RD.toLowerCase())}ms365detail`,
    m365BenefitDetails,
    true
  );
}

/**
 * Claims the Teams benefit for the user.
 * @param userProfileId the user profile to claim the benefit for.
 * @returns {IClaimBenefitDataResult}
 */
export async function claimUserProfileBenefitsTeams(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.RD.toLowerCase())}teams`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Gets the benefits for the user profile.
 * @param {number} userProfileId The profile id.
 * @returns {IProfileBenefitDataResult}
 */
export async function getProfileBenefits(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IProfileBenefitDataResult>>> {
  return await get<IApiResult2<IProfileBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.RD.toLowerCase())}${userProfileId}`,
    true
  );
}

/**
 * Gets a users M365 Benefit Details.
 * @param userProfileId the userprofile id to fetch the benefit details for.
 * @returns {IM365BenefitDetailDataResult}
 */
export async function getM365BenefitDetails(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IM365BenefitDetailDataResult>>> {
  return await get<IApiResult2<IM365BenefitDetailDataResult>>(
    `${BENEFIT_BASE_URL.replace(
      "{tenant}",
      TenantTypesEnum.RD.toLowerCase()
    )}m365detail/${userProfileId}`,
    true
  );
}

/**
 * Updates the users M365 Benefit Details that helps with renewing the M365 Benefit.
 * @param m365BenefitDetails the m365 subscription details to update/add.
 * @returns {IM365BenefitDetailDataResult}
 */
export async function updateM365BenefitDetails(
  m365BenefitDetails: IM365BenefitDetail
): Promise<IHttpResponse<IApiResult2<IM365BenefitDetailDataResult>>> {
  return await put<IApiResult2<IM365BenefitDetailDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.RD.toLowerCase())}m365detail`,
    m365BenefitDetails,
    true
  );
}

export async function extendAzureBenefitCredit(
  credit: number,
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IExtendAzureBenefitCreditDataResult>>> {
  return await put<IApiResult2<IExtendAzureBenefitCreditDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.RD.toLowerCase())}azure/extend`,
    { credit, userProfileId },
    true
  );
}

/**
 * Updates the user profile user flag.
 * @param userProfileId The user profile id.
 * @param isPrivate The user profile flag.
 */
export async function updateUserProfileIsPrivate(
  userProfileId: number,
  isPrivate: boolean
): Promise<IHttpResponse<IApiResult2<IApiEmptyResult>>> {
  return await post<IApiResult2<IApiEmptyResult>>(
    `${RD_PROFILE_BASE_URL}MarkPrivate`,
    { userProfileId, isPrivate },
    true
  );
}

/**
 * Updates the user profile primary email.
 * @param userProfileId The user profile id.
 * @param newEmail The new email.
 * @param requestorEmail The requestor email.
 */
export async function updateUserProfilePrimaryEmail(
  userProfileId: number,
  newEmail: string
): Promise<IHttpResponse<IApiResult2<IUserProfileDataResult>>> {
  return await put<IApiResult2<IUserProfileDataResult>>(
    `${RD_PROFILE_BASE_URL}UpdatePrimaryEmail`,
    { userProfileId, newEmail },
    true
  );
}

/**
 * Submits an Azure benefit claim for a user.
 * @param request The details of the Azure benefit claim.
 * @param userProfileId The ID of the user profile making the claim.
 */
export async function saveAzureBenefitClaim(
  request: IRDAzureBenefitClaim,
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<boolean>>> {
  return await post<IApiResult2<boolean>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.RD.toLowerCase())}azure/azureBenefit`,
    { ...request, userProfileId },
    true
  );
}
